import React from 'react';
import { Field, ErrorMessage } from 'formik';
import dayjs from 'dayjs';

/**
 * Reusable DateField component with Formik integration
 * Handles DD-MM-YYYY display format while maintaining proper date handling
 * 
 * @param {Object} props - Component props
 * @param {string} props.name - Formik field name
 * @param {string} props.label - Field label text
 * @param {boolean} props.required - Whether field is required (shows red asterisk)
 * @param {string} props.placeholder - Placeholder text (default: DD-MM-YYYY)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {Function} props.onChange - Custom onChange handler (optional)
 * @param {string} props.max - Maximum date in YYYY-MM-DD format (browser native constraint)
 * @param {string} props.min - Minimum date in YYYY-MM-DD format (browser native constraint)
 */
const DateField = ({
  name,
  label,
  required = false,
  placeholder = "DD-MM-YYYY",
  className = "",
  disabled = false,
  onChange = null,
  max,
  min
}) => {
  return (
    <div className="form-group">
      <label htmlFor={name} className="form-label">
        {required && <span style={{ color: "red", fontSize: "20px" }}>*</span>}
        {label}
      </label>
      
      <div className="input-group">
        <span className="input-group-text">
          <i className="fa fa-calendar"></i>
        </span>
        
        <Field name={name}>
          {({ field, form }) => (
            <input
              type="date"
              id={name}
              className={`form-control input-width ${className}`}
              disabled={disabled}
              max={max}
              min={min}
              
              // Convert DD-MM-YYYY to YYYY-MM-DD for date input
              value={
                field.value 
                  ? dayjs(field.value, 'DD-MM-YYYY').isValid() 
                    ? dayjs(field.value, 'DD-MM-YYYY').format('YYYY-MM-DD')
                    : field.value.includes('-') && field.value.length === 10
                    ? field.value // Already in YYYY-MM-DD format
                    : ''
                  : ''
              }
              
              onChange={(e) => {
                const dateValue = e.target.value; // YYYY-MM-DD from date input
                
                // Convert to DD-MM-YYYY format for storage
                let formattedDate = dateValue ? dayjs(dateValue).format('DD-MM-YYYY') : '';
                
                if (max && dateValue > max) {
                  formattedDate = dayjs(max).format('DD-MM-YYYY');
                }
                
                if (min && dateValue < min) {
                  formattedDate = dayjs(min).format('DD-MM-YYYY');
                }
                
                // Set the formatted value in Formik
                form.setFieldValue(name, formattedDate);
                
                // Call custom onChange if provided
                if (onChange) {
                  onChange(formattedDate, field, form);
                }
              }}
              
              onBlur={() => form.setFieldTouched(name, true)}
            />
          )}
        </Field>
      </div>
      
      <ErrorMessage name={name} component="div" className="text-danger" />
    </div>
  );
};

export default DateField;